import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X as XIcon, ShieldCheck, LockKey, CreditCard, EyeSlash, ArrowRight, Calculator, CalendarCheck, Quotes } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { publicTestimonials } from "@/lib/api";

/* ---------- animated count-up statistics ---------- */
const STATS = [
  { value: 12, suffix: "", label: "AI marketing employees on every plan" },
  { value: 31, suffix: " days", label: "of coordinated content in one click" },
  { value: 6, suffix: "+ hrs", label: "of marketing busywork saved weekly" },
  { value: 100, suffix: "%", label: "approval-gated — nothing posts without you" },
];

const CountUp = ({ target, suffix }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / 1200);
        setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target]);
  return <span ref={ref} className="font-head font-extrabold text-4xl md:text-5xl brand-text">{n}{suffix}</span>;
};

export const AnimatedStats = () => (
  <section className="border-y border-white/5 bg-[#0a0a0a]/60" data-testid="animated-stats">
    <div className="max-w-6xl mx-auto px-5 py-10 md:py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {STATS.map((s, i) => (
        <div key={i} className="text-center" data-testid={`stat-${i}`}>
          <CountUp target={s.value} suffix={s.suffix} />
          <div className="mt-2 text-xs md:text-sm text-[#9a9a9a] leading-snug max-w-[200px] mx-auto">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- before vs after ---------- */
const BEFORE = ["Sunday nights spent writing posts", "Weeks of silence between posts", "Emails you meant to send… someday",
  "Paying for 7 tools that don't talk", "Guessing what content works", "Marketing stops when you get busy"];
const AFTER = ["Your month planned & written in one click", "Auto Pilot publishes on schedule, every day", "Email campaigns drafted, scheduled, sent",
  "One platform, one login, one AI team", "A strategy mix tuned to your industry", "Marketing runs even on your busiest week"];

export const BeforeAfter = () => (
  <section className="max-w-6xl mx-auto px-5 py-16 md:py-24" data-testid="before-after-section">
    <div className="text-center max-w-2xl mx-auto mb-10">
      <div className="label-caps mb-3">The difference</div>
      <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Life before Vozelto. <span className="brand-text">Life after.</span></h2>
    </div>
    <div className="grid md:grid-cols-2 gap-6 items-stretch">
      <div className="card-matte p-7 h-full" data-testid="before-card">
        <div className="label-caps mb-4 text-[#7a7a7a]">Before — marketing on hard mode</div>
        <ul className="space-y-3">
          {BEFORE.map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-[#9a9a9a]">
              <XIcon size={15} className="text-red-400/70 shrink-0 mt-0.5" weight="bold" /> {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="card-matte brand-glow p-7 h-full relative overflow-hidden" data-testid="after-card">
        <div className="absolute -top-16 right-0 h-40 w-40 rounded-full bg-[#6B3DFF]/15 blur-[80px] pointer-events-none" />
        <div className="label-caps mb-4 brand-text">After — marketing that runs itself</div>
        <ul className="space-y-3">
          {AFTER.map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-[#e6e6e6]">
              <Check size={15} className="brand-text shrink-0 mt-0.5" weight="bold" /> {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ---------- feature comparison table ---------- */
const COMPARE_ROWS = [
  ["Complete monthly campaign in one click", false, false, true],
  ["12 coordinated AI specialists", false, false, true],
  ["Publishes directly to Facebook & Instagram", true, true, true],
  ["Knows your brand, products & voice", false, true, true],
  ["Approval queue — you control everything", false, false, true],
  ["Industry content strategy & goal-driven CTAs", false, true, true],
  ["Real photo library + honest AI images", false, false, true],
  ["Shopify, GA4 & Klaviyo connected", false, false, true],
  ["Monthly cost", "$200–500 in tools", "$1,500–5,000 retainer", "From $49/mo"],
];

export const ComparisonTable = () => (
  <section className="border-y border-white/5 bg-[#0a0a0a]/60" data-testid="comparison-table-section">
    <div className="max-w-5xl mx-auto px-5 py-16 md:py-24">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="label-caps mb-3">Compare your options</div>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">DIY tools vs. an agency vs. <span className="brand-text">Vozelto</span></h2>
      </div>
      <div className="card-matte overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]" data-testid="comparison-table">
          <thead>
            <tr className="border-b border-white/8 text-left">
              <th className="px-5 py-4 font-head text-[#9a9a9a] font-semibold text-xs uppercase tracking-wider">What you get</th>
              <th className="px-4 py-4 font-head text-[#9a9a9a] font-semibold text-xs uppercase tracking-wider text-center">DIY tools</th>
              <th className="px-4 py-4 font-head text-[#9a9a9a] font-semibold text-xs uppercase tracking-wider text-center">Agency</th>
              <th className="px-4 py-4 text-center">
                <span className="inline-block rounded-full bg-[#6B3DFF] text-white text-[10px] font-bold px-3 py-1">VOZELTO</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(([label, diy, agency, voz], i) => (
              <tr key={i} className={`border-b border-white/5 last:border-0 ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                <td className="px-5 py-3.5 text-[#cfcfcf]">{label}</td>
                {[diy, agency, voz].map((v, j) => (
                  <td key={j} className={`px-4 py-3.5 text-center ${j === 2 ? "bg-[#6B3DFF]/[0.06]" : ""}`}>
                    {v === true ? <Check size={17} className={j === 2 ? "brand-text inline" : "text-emerald-400/60 inline"} weight="bold" />
                      : v === false ? <XIcon size={15} className="text-white/20 inline" weight="bold" />
                        : <span className={`text-xs ${j === 2 ? "brand-text font-bold" : "text-[#7a7a7a]"}`}>{v}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

/* ---------- ROI calculator ---------- */
export const RoiCalculator = () => {
  const navigate = useNavigate();
  const [hours, setHours] = useState(8);
  const [rate, setRate] = useState(50);
  const [agency, setAgency] = useState(0);
  const timeSaved = Math.round(hours * 0.85 * 10) / 10;
  const monthlyValue = Math.round(timeSaved * 4.33 * rate) + Number(agency || 0);
  const planCost = 129;
  const roi = monthlyValue > 0 ? Math.round(((monthlyValue - planCost) / planCost) * 100) : 0;
  const Slider = ({ label, value, onChange, min, max, step = 1, fmt }) => (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-[#9a9a9a]">{label}</span>
        <span className="font-head font-bold text-white">{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#6B3DFF] cursor-pointer" />
    </div>
  );
  return (
    <section className="max-w-6xl mx-auto px-5 py-16 md:py-24" data-testid="roi-calculator-section">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="label-caps mb-3 flex items-center gap-2"><Calculator size={15} className="brand-text" weight="duotone" /> ROI calculator</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">What is your time actually worth?</h2>
          <p className="mt-3 text-[#9a9a9a] leading-relaxed">Vozelto customers automate roughly 85% of their weekly marketing busywork. Slide to match your business and see the math for yourself.</p>
          <div className="mt-8 space-y-6">
            <Slider label="Hours you spend on marketing / week" value={hours} onChange={setHours} min={1} max={30} fmt={(v) => `${v} hrs`} />
            <Slider label="What an hour of your time is worth" value={rate} onChange={setRate} min={15} max={250} step={5} fmt={(v) => `$${v}`} />
            <Slider label="Current agency / freelancer spend per month" value={agency} onChange={setAgency} min={0} max={5000} step={100} fmt={(v) => `$${v}`} />
          </div>
        </div>
        <div className="card-matte brand-glow p-8 relative overflow-hidden" data-testid="roi-result-card">
          <div className="absolute -top-16 right-0 h-40 w-40 rounded-full bg-[#6B3DFF]/15 blur-[80px] pointer-events-none" />
          <div className="label-caps mb-5 brand-text">Your estimated return</div>
          <div className="space-y-4">
            <div className="flex items-end justify-between border-b border-white/8 pb-4">
              <span className="text-sm text-[#9a9a9a]">Time handed back to you</span>
              <span className="font-head font-extrabold text-2xl" data-testid="roi-time-saved">{timeSaved} hrs/wk</span>
            </div>
            <div className="flex items-end justify-between border-b border-white/8 pb-4">
              <span className="text-sm text-[#9a9a9a]">Monthly value recovered</span>
              <span className="font-head font-extrabold text-2xl" data-testid="roi-monthly-value">${monthlyValue.toLocaleString()}</span>
            </div>
            <div className="flex items-end justify-between">
              <span className="text-sm text-[#9a9a9a]">Return vs. Growth plan (${planCost}/mo)</span>
              <span className="font-head font-extrabold text-3xl brand-text" data-testid="roi-percent">{roi > 0 ? `${roi.toLocaleString()}%` : "—"}</span>
            </div>
          </div>
          <Button data-testid="roi-start-trial" onClick={() => navigate("/signup")} className="mt-7 w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-12">
            Claim this ROI — Start Free Trial <ArrowRight size={16} className="ml-1.5" weight="bold" />
          </Button>
          <p className="mt-3 text-[10px] text-[#5a5a5a] text-center">Estimate based on automating ~85% of routine marketing tasks. Your results depend on usage.</p>
        </div>
      </div>
    </section>
  );
};

/* ---------- trust & security badges ---------- */
const TRUST = [
  { icon: CreditCard, title: "Stripe-secured billing", desc: "Payments handled by Stripe — we never see or store your card." },
  { icon: LockKey, title: "Isolated workspace data", desc: "Every business's data is tenant-isolated with role-based access control." },
  { icon: ShieldCheck, title: "Approval-gated publishing", desc: "Nothing goes live without your explicit sign-off. Ever." },
  { icon: EyeSlash, title: "Privacy by default", desc: "Photo EXIF data stripped on upload. Your data is never sold or shared." },
];

export const TrustSecurity = () => (
  <section className="border-y border-white/5 bg-[#0a0a0a]/60" data-testid="trust-security-section">
    <div className="max-w-6xl mx-auto px-5 py-14 md:py-20">
      <div className="text-center mb-10">
        <div className="label-caps mb-3">Trust & security</div>
        <h2 className="font-head font-bold text-2xl md:text-3xl tracking-tight">Your business is safe here</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TRUST.map((t, i) => (
          <div key={t.title} data-testid={`trust-badge-${i}`} className="card-matte p-5 text-center">
            <div className="h-11 w-11 mx-auto rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center mb-3">
              <t.icon size={22} className="brand-text" weight="duotone" />
            </div>
            <div className="font-head font-semibold text-sm">{t.title}</div>
            <p className="mt-1.5 text-xs text-[#9a9a9a] leading-relaxed">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- testimonials (API-driven, sample-labelled until replaced) ---------- */
export const TestimonialsSection = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { publicTestimonials().then(setRows).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-5 py-16 md:py-24" data-testid="testimonials-section">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="label-caps mb-3">What owners say</div>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Built for businesses like yours</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {rows.slice(0, 6).map((t, i) => (
          <div key={t.id} data-testid={`testimonial-${i}`} className="card-matte p-6 h-full relative">
            {t.placeholder && (
              <span className="absolute top-3 right-3 text-[9px] font-bold rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25 px-2 py-0.5" data-testid={`testimonial-sample-badge-${i}`}>SAMPLE</span>
            )}
            <Quotes size={24} className="brand-text mb-3" weight="fill" />
            <p className="text-sm text-[#cfcfcf] leading-relaxed italic">“{t.quote}”</p>
            {t.results && <div className="mt-3 text-xs font-semibold brand-text">{t.results}</div>}
            <div className="mt-5 flex items-center gap-3">
              {t.photo_url
                ? <img src={t.photo_url} alt={t.name} className="h-10 w-10 rounded-full object-cover border border-white/10" loading="lazy" />
                : <div className="h-10 w-10 rounded-full bg-[#6B3DFF]/15 border border-[#6B3DFF]/25 flex items-center justify-center font-head font-bold text-sm brand-text">{(t.name || "?").slice(0, 1)}</div>}
              <div>
                <div className="text-sm font-semibold text-white">{t.name}</div>
                <div className="text-xs text-[#7a7a7a]">{t.role ? `${t.role}, ` : ""}{t.company}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {rows.some((t) => t.placeholder) && (
        <p className="mt-4 text-center text-[10px] text-[#5a5a5a]" data-testid="testimonials-sample-note">Entries marked SAMPLE are illustrative and will be replaced by verified customers.</p>
      )}
    </section>
  );
};

/* ---------- mid-page CTA band ---------- */
export const CtaBand = ({ onBookDemo, headline = "See your AI team in action" }) => {
  const navigate = useNavigate();
  return (
    <section className="max-w-6xl mx-auto px-5 py-8" data-testid="cta-band">
      <div className="card-matte px-6 py-6 md:px-10 flex flex-wrap items-center gap-4 justify-between border border-[#6B3DFF]/20">
        <div>
          <div className="font-head font-bold text-lg">{headline}</div>
          <div className="text-sm text-[#9a9a9a]">14-day free trial · no credit card tricks · cancel anytime</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="cta-band-trial" onClick={() => navigate("/signup")} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-6 h-11">Start Free Trial</Button>
          <Button data-testid="cta-band-demo" variant="ghost" onClick={onBookDemo} className="text-white hover:bg-white/5 rounded-full px-5 h-11 border border-white/10">
            <CalendarCheck size={16} className="mr-2 brand-text" /> Book a Demo
          </Button>
        </div>
      </div>
    </section>
  );
};

/* ---------- sticky CTA bar (appears after scroll) ---------- */
export const StickyCtaBar = ({ onBookDemo }) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div data-testid="sticky-cta-bar"
      className={`fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}>
      <div className="mx-auto max-w-6xl px-3 pb-3">
        <div className="rounded-2xl border border-white/10 bg-[#0d0d13]/95 backdrop-blur-xl px-4 py-3 flex items-center gap-3 shadow-[0_-8px_40px_rgba(0,0,0,0.5)]">
          <span className="hidden sm:block text-sm text-[#cfcfcf] font-semibold flex-1">Your AI marketing team is ready to start today.</span>
          <Button data-testid="sticky-book-demo" variant="ghost" onClick={onBookDemo}
            className="flex-1 sm:flex-none text-white hover:bg-white/5 rounded-full h-10 px-4 border border-white/15 text-sm">
            <CalendarCheck size={15} className="mr-1.5 brand-text" /> Book Demo
          </Button>
          <Button data-testid="sticky-start-trial" onClick={() => navigate("/signup")}
            className="flex-1 sm:flex-none bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5 text-sm">
            Start Free Trial <ArrowRight size={14} className="ml-1" weight="bold" />
          </Button>
        </div>
      </div>
    </div>
  );
};
